import type { AdminOrderResponse } from '@inflip/contracts/admin';
import { DepositReturnKind, IP_RIGHT_LABELS, OrderStatus } from '@inflip/shared';
import { useState } from 'react';

import { ConfirmDepositDialog, refundBankOf, won } from './confirm-deposit-dialog';
import { api } from '../lib/api-client';
import { showToast } from '../lib/toast';

type GroupMember = NonNullable<AdminOrderResponse['groupOrders']>[number];

/**
 * 입금 대기 묶음의 (취소되지 않은) 주문 — 입금 확인·취소가 이 주문 전체에 적용된다 (서버 confirmGroup·cancelGroup).
 * 2건 이상일 때만 묶음으로 본다 (주문은 1건이어도 묶음으로 만들어진다)
 */
export function pendingGroupMembers(order: AdminOrderResponse): GroupMember[] {
  return order.payment?.status === 'PENDING'
    ? (order.groupOrders ?? []).filter((g) => g.status !== OrderStatus.CANCELLED)
    : [];
}

/**
 * 입금 확인을 할 수 없게 바뀐 주문의 안내 문구 — 아직 입금 대기면 null.
 * 화면을 본 뒤 구매자 취소·기한 만료·다른 어드민 처리로 상태가 바뀐 경우
 */
export function paymentNoLongerPendingMessage(order: AdminOrderResponse): string | null {
  if (order.status === OrderStatus.PAYMENT_PENDING && order.payment?.status === 'PENDING')
    return null;
  if (order.status === OrderStatus.CANCELLED) {
    if (order.cancelledBy === 'USER')
      return '구매자가 이미 주문을 취소했어요. 최신 상태로 다시 불러왔어요.';
    if (order.cancelledBy === 'SYSTEM')
      return '입금 기한이 지나 이미 취소된 주문이에요. 최신 상태로 다시 불러왔어요.';
    return '이미 취소된 주문이에요. 최신 상태로 다시 불러왔어요.';
  }
  return '이미 입금 확인이 끝난 주문이에요. 최신 상태로 다시 불러왔어요.';
}

/** 확인 창 안에 함께 처리되는 묶음 주문 목록 */
export function GroupOrdersBox({
  heading,
  orders,
  currentId,
}: {
  heading: string;
  orders: GroupMember[];
  currentId: string;
}) {
  return (
    <div className="mt-3 rounded-md bg-muted px-3.5 py-3 text-sm">
      <p className="font-semibold">{heading}</p>
      <ul className="mt-1.5 space-y-1">
        {orders.map((g) => (
          <li key={g.id} className="flex justify-between gap-3">
            <span>
              {IP_RIGHT_LABELS[g.rightType]}
              {g.id === currentId && <span className="text-muted-foreground"> (이 주문)</span>}
            </span>
            <span className="tabular-nums">{won(g.contractAmount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 주문 입금 확인 창 — 주문 상세와 주문 목록이 같이 쓴다.
 * order 는 상세 응답이어야 한다 (groupOrders 로 함께 확인되는 묶음 주문을 보여 준다).
 * 서버가 거절하면 창을 닫지 않고 오류를 보여 준다. onDone 은 처리 성공·실패 모두 지금 상태를 다시 불러오라는 뜻
 */
export function OrderConfirmPaymentDialog({
  order,
  token,
  onDone,
  onClose,
}: {
  order: AdminOrderResponse & { payment: NonNullable<AdminOrderResponse['payment']> };
  token: string;
  onDone(ok: boolean): Promise<unknown>;
  onClose(): void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const members = pendingGroupMembers(order);
  const grouped = members.length > 1;

  async function run(fn: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onDone(true);
    } catch (err) {
      // 그사이 구매자 취소·기한 만료·다른 어드민 처리로 입금 대기가 아니게 됐으면 창을 닫고 알린다
      const latest = await api.getOrder(token, order.id).catch(() => null);
      const stale = latest && paymentNoLongerPendingMessage(latest);
      if (stale) {
        showToast(stale);
        onClose();
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      // 지금 상태로 다시 그린다
      await onDone(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmDepositDialog
      title={grouped ? `입금 확인 · 묶음 주문 ${members.length}건` : '입금 확인'}
      expectedAmount={order.payment.amount}
      expectedDepositor={order.payment.depositReference}
      note={
        grouped
          ? `묶음 주문이라 함께 주문한 ${members.length}건이 같이 처리돼요. 확인하면 모두 결제 확인 상태가 되고 거래 채팅방이 열려요.`
          : '확인하면 주문이 결제 확인 상태가 되고 거래 채팅방이 열려요.'
      }
      underpaymentNote={
        grouped ? `함께 주문한 ${members.length}건이 모두 취소돼요.` : '주문은 취소돼요.'
      }
      allowOverpayment
      defaultBankAccount={order.buyerRefundAccount ? refundBankOf(order.buyerRefundAccount) : null}
      busy={busy}
      error={error}
      onSubmit={(input) =>
        void run(() =>
          api.confirmPayment(token, order.id, {
            depositReference: input.depositorName,
            receivedAmount: input.receivedAmount,
            ...(input.overrideReason ? { depositorOverrideReason: input.overrideReason } : {}),
            ...(input.overpayment ? { overpayment: input.overpayment } : {}),
          }),
        )
      }
      onUnderpayment={(input) =>
        void run(() =>
          api.returnDeposit(token, order.id, { kind: DepositReturnKind.UNDERPAYMENT, ...input }),
        )
      }
      onClose={onClose}
    >
      {grouped && (
        <GroupOrdersBox
          heading={`함께 확인되는 주문 ${members.length}건`}
          orders={members}
          currentId={order.id}
        />
      )}
    </ConfirmDepositDialog>
  );
}
